import React, { useState, useEffect, useMemo } from 'react';
import { Table, message } from 'antd';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Search, FileText } from 'lucide-react';
import moment from 'moment';
import { useIntl } from 'react-intl';
import ExportLabelModal from './ExportLabelModal';
import PaginationTable from '../../../components/pagination/Pagination';
import PopupContext from '../../../components/popupContext';
import {
  actionGetStudies,
  actionGetTotalStatus,
  actionCreateSession,
} from '../StudyListAction';
import { actionShowUploadModal } from '../../system/systemAction';
import {
  STUDY_STATUS,
  SESSION_TYPE,
  REDIRECT_VIEWER_URL,
} from '../../../utils/constants/config';
import './Data.scss';
import DeleteStudyModal from './DeleteStudyModal';
import AssignLabelerModal from './AssignLabelerModal';

let params = { _offset: 0, _limit: 25 };

const TYPE_MODAL = {
  EXPORT_LABEL: 1,
  DELETE_STUDY: 2,
  ASSIGN_LABELER: 3,
};

const STATUS_DOT_COLORS = {
  count: '#2a5f5a',
  ASSIGNED: '#2a5f5a',
  UNASSIGNED: '#60a5fa',
  COMPLETED: '#34d399',
};

const STATUS_ACTIVE_STYLES = {
  count: {
    background: 'rgba(42,95,90,0.1)',
    border: '1px solid rgba(42,95,90,0.3)',
    color: '#2a5f5a',
  },
  ASSIGNED: {
    background: 'rgba(42,95,90,0.1)',
    border: '1px solid rgba(42,95,90,0.3)',
    color: '#2a5f5a',
  },
  UNASSIGNED: {
    background: 'rgba(59,130,246,0.1)',
    border: '1px solid rgba(59,130,246,0.3)',
    color: '#60a5fa',
  },
  COMPLETED: {
    background: 'rgba(16,185,129,0.1)',
    border: '1px solid rgba(16,185,129,0.3)',
    color: '#34d399',
  },
};

const BADGE_STYLES = {
  assigned: {
    background: 'rgba(42,95,90,0.3)',
    color: '#84bfb8',
    border: '1px solid rgba(42,95,90,0.4)',
  },
  unassigned: {
    background: 'rgba(107,114,128,0.3)',
    color: '#d1d5db',
    border: '1px solid rgba(107,114,128,0.4)',
  },
  completed: {
    background: 'rgba(16,185,129,0.3)',
    color: '#6ee7b7',
    border: '1px solid rgba(16,185,129,0.4)',
  },
};

const Data = (props) => {
  const {
    studies,
    isFetching,
    currentProject,
    projectId = '',
    totalStatus = {},
  } = props;

  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowItems, setSelectedRowItems] = useState([]);
  const intl = useIntl();
  const { formatMessage: t } = intl;
  const [popupContext, setPopupContext] = useState({ visible: false });
  const [selectedStatus, setSelectedStatus] = useState(STUDY_STATUS.ALL);
  const [typeModal, setTypeModal] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    handleGetTotalStatus();
    return () => {
      params = { _offset: 0, _limit: 25 };
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (projectId) {
      params = {
        ...params,
        project_id: projectId,
      };
      handleFetchData(params);
    }
    // eslint-disable-next-line
  }, [projectId]);

  // Refresh the study list immediately after a successful 'Add to Project' upload
  // without a full page reload.
  useEffect(() => {
    const onStudyUploaded = () => {
      if (projectId) handleFetchData({ ...params, project_id: projectId });
    };
    window.addEventListener('project-study-uploaded', onStudyUploaded);
    return () => window.removeEventListener('project-study-uploaded', onStudyUploaded);
    // eslint-disable-next-line
  }, [projectId]);

  const handleFetchData = (newParams = {}) => {
    props.actionShowUploadModal({ studyParams: newParams });
    props.actionGetStudies({
      ...newParams,
      _offset: newParams._offset * newParams._limit,
    });
  };

  const handleGetTotalStatus = () => {
    props.actionGetTotalStatus({ project_id: projectId });
  };

  const taskStatus = useMemo(() => {
    return [
      {
        id: STUDY_STATUS.ALL,
        name: t({ id: 'IDS_ALL' }),
        total: totalStatus?.[STUDY_STATUS.ALL] || 0,
      },
      {
        id: STUDY_STATUS.ASSIGNED,
        name: t({ id: 'IDS_ASSIGNED' }),
        total: totalStatus?.agg?.status?.[STUDY_STATUS.ASSIGNED] || 0,
      },
      {
        id: STUDY_STATUS.UNASSIGNED,
        name: t({ id: 'IDS_UNASSIGNED' }),
        total: totalStatus?.agg?.status?.[STUDY_STATUS.UNASSIGNED] || 0,
      },
      {
        id: STUDY_STATUS.COMPLETED,
        name: t({ id: 'IDS_COMPLETED' }),
        total: totalStatus?.agg?.status?.[STUDY_STATUS.COMPLETED] || 0,
      },
    ];
  }, [totalStatus, t]);

  const columns = [
    {
      title: t({ id: 'IDS_STUDY_CODE' }),
      dataIndex: 'code',
      key: 'code',
      ellipsis: true,
      sorter: true,
      showSorterTooltip: false,
    },
    {
      title: t({ id: 'IDS_STUDY_UID' }),
      dataIndex: 'dicom_tags.StudyInstanceUID',
      key: 'dicom_tags.StudyInstanceUID',
      ellipsis: true,
      render: (_, record) => {
        const studyInstanceUID = record?.dicom_tags?.StudyInstanceUID || [];
        return (
          <span>{studyInstanceUID.length > 0 ? studyInstanceUID[0] : ''}</span>
        );
      },
    },
    {
      title: t({ id: 'IDS_LAST_ACTIVITY' }),
      dataIndex: 'modified',
      key: 'modified',
      sorter: true,
      ellipsis: true,
      showSorterTooltip: false,
      width: 220,
      align: 'center',
      render: (txt) => (
        <span>{txt ? moment(txt).format('YYYY-MM-DD HH:mm') : ''}</span>
      ),
    },
    {
      title: t({ id: 'IDS_STATUS' }),
      dataIndex: 'status',
      key: 'status',
      width: 160,
      align: 'center',
      render: (status) => {
        const key = status?.toLowerCase();
        return (
          <span
            className="study-status-badge"
            style={BADGE_STYLES[key] || {}}
          >
            {status}
          </span>
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (rowKeys, rowItems) => {
      setSelectedRowKeys(rowKeys);
      setSelectedRowItems(rowItems);
    },
  };

  const resetSelectedItem = () => {
    setSelectedRowKeys([]);
    setSelectedRowItems([]);
  };

  const handleRefreshData = () => {
    resetSelectedItem();
    handleFetchData(params);
    handleGetTotalStatus();
  };

  const showDeleteModal = () => {
    setTypeModal(TYPE_MODAL.DELETE_STUDY);
  };

  const handleCreateSession = async (study) => {
    if (processing) return;
    try {
      let listStudyUID = [];
      if (study) {
        const rawStudyUID = (study?.dicom_tags?.StudyInstanceUID || [])[0];
        const sessionId = rawStudyUID || study?.id;
          
        listStudyUID = [
          {
            type: SESSION_TYPE.STUDY,
            id: sessionId,
            meta: {
              project_id: study?.project_id,
              study_code: study?.code,
              study_id: study?.id,
              project_name: currentProject?.name || '',
            },
          },
        ];
      } else {
        listStudyUID = selectedRowItems.map((item) => {
          const rawStudyUID = (item?.dicom_tags?.StudyInstanceUID || [])[0];
          const sessionId = rawStudyUID || item?.id;
            
          return {
            type: SESSION_TYPE.STUDY,
            id: sessionId,
            meta: {
              project_id: item?.project_id,
              study_code: item?.code,
              study_id: item?.id,
              project_name: currentProject?.name || '',
            },
          };
        });
      }
      setProcessing(true);
      const { data } = await actionCreateSession({ data: listStudyUID });
      setProcessing(false);
      const { session_id } = data?.data || {};
      if (session_id) {
        const studyUID = listStudyUID[0]?.id || '';
        window.open(
          '/afya-annotation-viewer/viewer?StudyInstanceUIDs=' + encodeURIComponent(studyUID) + '&sessionId=' + session_id + '&time=' + Date.now(),
          '_blank'
        );
      }
    } catch (error) {
      console.log(error);
      setProcessing(false);
      message.error('Error!');
    }
  };

  const onChangePagination = (page, size) => {
    params = { ...params, _offset: page - 1, _limit: size };
    resetSelectedItem();
    handleFetchData(params);
  };

  const handleTableChange = (_, filters, sorter) => {
    if (sorter?.order) {
      params._sort =
        sorter?.order === 'ascend' ? sorter.field : `-${sorter.field}`;
    } else {
      delete params._sort;
    }
    resetSelectedItem();
    handleFetchData(params);
  };

  const handleSearch = (e) => {
    if (e.keyCode === 13) {
      const value = e.target.value;
      if (value.trim()) {
        params = { ...params, _search: value.trim() };
      } else {
        delete params._search;
      }
      params._offset = 0;
      resetSelectedItem();
      handleFetchData(params);
    }
  };

  // Unused viewer actions kept in code for future re-enablement:
  // v3 Viewer      → /afya-viewer/viewer?StudyInstanceUIDs=...
  // v3 Segmentation → /afya-viewer/segmentation?StudyInstanceUIDs=...
  // annotation-v3  → /afya-annotation-viewer/viewer (same as Open below)

  const buttonList = [
    {
      id: 1,
      name: t({ id: 'IDS_OPEN' }),
      isDisable: selectedRowKeys?.length === 0,
      // Opens AfyaNX Annotation Viewer (OHIF v3) — same path as double-click
      action: handleCreateSession,
    },
    {
      id: 2,
      name: t({ id: 'IDS_DELETE' }),
      isDisable: selectedRowKeys?.length === 0,
      danger: true,
      action: showDeleteModal,
    },
  ];

  const onFilterStatus = (status = {}) => {
    if (selectedStatus === status.id) return;
    setSelectedStatus(status.id);
    params._offset = 0;
    if (status.id === STUDY_STATUS.ALL) {
      delete params.status;
    } else {
      params.status = status.id;
    }
    resetSelectedItem();
    handleFetchData(params);
  };

  return (
    <div className="data-page">
      {/* Action Buttons + Export Label — outside table, on main page background */}
      <div className="outer-action-bar">
        <div className="outer-action-left">
          {selectedRowKeys.length > 0 && (
            <>
              <button
                className="btn-action btn-teal"
                onClick={() => setTypeModal(TYPE_MODAL.ASSIGN_LABELER)}
              >
                {t({ id: 'IDS_ASSIGN' })} ({selectedRowKeys.length})
              </button>
              {buttonList.map((button) => (
                <button
                  key={button.id}
                  className={`btn-action ${
                    button.danger ? 'btn-danger' : 'btn-teal'
                  }`}
                  disabled={button.isDisable}
                  onClick={() => button.action()}
                >
                  {button.name} ({selectedRowKeys.length})
                </button>
              ))}
            </>
          )}
        </div>
        <button
          className="btn-action btn-teal btn-export"
          onClick={() => setTypeModal(TYPE_MODAL.EXPORT_LABEL)}
        >
          <FileText size={14} />
          {t({ id: 'IDS_EXPORT_LABEL' })}
        </button>
      </div>

      <div className="data-container">
        {/* Left Sidebar */}
        <div className="sidebar-panel">
          <div className="sidebar-card">
            <div className="card-title">{t({ id: 'IDS_STATUS' })}</div>
            <div className="status-list">
              {taskStatus.map((it) => {
                const isActive = selectedStatus === it.id;
                const dotColor = STATUS_DOT_COLORS[it.id] || '#2a5f5a';
                const activeStyle = isActive
                  ? STATUS_ACTIVE_STYLES[it.id] || {}
                  : {};
                return (
                  <div
                    className={`status-item ${isActive ? 'is-active' : ''}`}
                    key={it.id}
                    style={{
                      ...activeStyle,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      padding: '8px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      transition: 'all 0.2s',
                    }}
                    onClick={() => onFilterStatus(it)}
                  >
                    <span
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: dotColor,
                        marginRight: '10px',
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        flex: 1,
                        color: isActive
                          ? (activeStyle.color || '#2a5f5a')
                          : '#9ca3af',
                      }}
                    >
                      {it.name}
                    </span>
                    <span
                      style={{
                        fontWeight: 600,
                        color: isActive
                          ? (activeStyle.color || '#2a5f5a')
                          : '#9ca3af',
                      }}
                    >
                      {it.total}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

        </div>

        {/* Right Table Panel */}
        <div className="table-panel">
          {/* Table Header Controls */}
          <div className="table-controls">
            <div className="controls-left">
              <div className="search-wrapper">
                <Search size={14} color="#9ca3af" />
                <input
                  className="search-input"
                  placeholder={t({ id: 'IDS_SEARCH' })}
                  onKeyDown={handleSearch}
                />
              </div>
              <div className="select-info">
                <span className="select-label">Select:</span>
                <span
                  className={`select-count ${
                    selectedRowKeys.length > 0 ? 'active' : ''
                  }`}
                >
                  {selectedRowKeys.length || 0}
                </span>
                <span className="select-total">
                  {' '}
                  / {studies?.count || 0}
                </span>
              </div>
            </div>
            <div className="controls-right">
              <span className="rows-label">Rows:</span>
              <PaginationTable
                page={params._offset}
                size={params._limit}
                onChange={onChangePagination}
                totalElements={studies.count || 0}
                defaultPageSize={params._limit}
                onChangePageSize={(value) => {
                  params._offset = 0;
                  params._limit = value;
                  resetSelectedItem();
                  handleFetchData(params);
                }}
              />
            </div>
          </div>

          <Table
            className="figma-data-table"
            size="small"
            loading={isFetching}
            rowKey={(record) => record.id}
            rowSelection={rowSelection}
            dataSource={studies.data || []}
            columns={columns}
            pagination={false}
            /* Cap visible body height at ~9 rows so the container size
             * stays predictable regardless of how many studies the page
             * holds.  Antd's `scroll.y` keeps the table header sticky
             * and makes only the body region scroll. */
            scroll={{ y: 520 }}
            onChange={handleTableChange}
            onRow={(record) => {
              return {
                onContextMenu: (event) => {
                  event.preventDefault();
                  if (selectedRowKeys.length === 0) return;

                  document.addEventListener(`click`, function onClickOutside() {
                    setPopupContext({
                      visible: false,
                    });
                    document.removeEventListener(`click`, onClickOutside);
                  });
                  setPopupContext({
                    visible: true,
                    x: event.clientX,
                    y: event.clientY,
                  });
                },
                onDoubleClick: (event) => {
                  event.stopPropagation();
                  event.preventDefault();
                  if (event?._targetInst?.elementType === 'input') {
                    return;
                  }
                  handleCreateSession(record);
                },
              };
            }}
          />

          {/* Pagination at bottom of table container */}
          {studies?.count > 0 && (
            <div className="table-pagination">
              <span className="pagination-info">
                Showing{' '}
                <span className="pagination-bold">
                  {params._offset * params._limit + 1}
                </span>{' '}
                to{' '}
                <span className="pagination-bold">
                  {Math.min(
                    (params._offset + 1) * params._limit,
                    studies.count
                  )}
                </span>{' '}
                of{' '}
                <span className="pagination-bold">{studies.count}</span>
              </span>
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  disabled={params._offset === 0}
                  onClick={() =>
                    onChangePagination(params._offset, params._limit)
                  }
                >
                  &#8249;
                </button>
                <span className="pagination-page">
                  Page {params._offset + 1} of{' '}
                  {Math.ceil(studies.count / params._limit) || 1}
                </span>
                <button
                  className="pagination-btn"
                  disabled={
                    (params._offset + 1) * params._limit >= studies.count
                  }
                  onClick={() =>
                    onChangePagination(params._offset + 2, params._limit)
                  }
                >
                  &#8250;
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {typeModal === TYPE_MODAL.EXPORT_LABEL && (
        <ExportLabelModal
          projectId={projectId}
          onOk={() => setTypeModal(null)}
          onCancel={() => setTypeModal(null)}
        />
      )}
      {typeModal === TYPE_MODAL.DELETE_STUDY && (
        <DeleteStudyModal
          selectedRowKeys={selectedRowKeys}
          selectedRowItems={selectedRowItems}
          onCancel={(needResfresh) => {
            if (needResfresh) {
              params._offset = 0;
              handleRefreshData();
            }
            setTypeModal(null);
          }}
        />
      )}
      {typeModal === TYPE_MODAL.ASSIGN_LABELER && (
        <AssignLabelerModal
          onCancel={(needResfresh) => {
            if (needResfresh) {
              handleRefreshData();
            }
            setTypeModal(null);
          }}
          searchCondition={params}
          selectedItem={selectedRowKeys}
          currentProject={currentProject}
        />
      )}
      <PopupContext
        buttonList={[
          {
            id: 3,
            name: t({ id: 'IDS_ASSIGN' }),
            action: () => {
              setTypeModal(TYPE_MODAL.ASSIGN_LABELER);
            },
          },
          ...buttonList,
        ]}
        {...popupContext}
      />
    </div>
  );
};

export default connect(
  (state) => ({
    studies: state.study.studies,
    isFetching: state.study.isFetching,
    totalStatus: state.study.totalStatus,
    currentProject: state.project.currentProject,
    uploadInfoModal: state.system.uploadInfoModal,
    userInfo: state.system.profile,
  }),
  { actionGetStudies, actionShowUploadModal, actionGetTotalStatus }
)(withRouter(Data));
